import { Link } from "@tanstack/react-router";
import { getImageUrl, getThreadTitle } from "@/entities/thread";
import { useHistoryThreads } from "@/features/history/hooks/useHistoryThreads";
import { useHistoryStore } from "@/features/history/stores/historyStore";
import { useDesktopUiStore } from "@/shared/stores/desktopUiStore";

/**
 * Desktop の左カラム History Sidebar 本実装。
 *
 * - 幅 310px (Figma `Asset - Sidebar/Desktop and Tablet` 確定値)
 * - `historyStore.getViewedItems()` から最大 20 件
 * - `useHistoryThreads` で各エントリの board の API から fetch (board またぎ対応)
 * - 開閉状態は `desktopUiStore.historySidebarOpen` で persist
 *
 * mobile SideMenu の history 部分のロジックを再利用しているが、UI は Desktop 用に
 * 再構成。drawer ではなく persistent 左パネル。
 */
export const HistorySidebar: React.FunctionComponent = () => {
  const isOpen = useDesktopUiStore((s) => s.historySidebarOpen);
  const { getViewedItems } = useHistoryStore();
  const viewedItems = getViewedItems().slice(0, 20);
  const {
    data: historyThreads = [],
    isLoading,
    isError,
  } = useHistoryThreads(viewedItems, isOpen && viewedItems.length > 0);

  if (!isOpen) return null;

  return (
    <aside className="w-77.5 flex-none border-r border-border bg-card overflow-y-auto">
      <header className="px-4 py-2 text-sm font-medium text-muted-foreground sticky top-0 bg-card border-b border-border">
        閲覧履歴
      </header>
      {viewedItems.length === 0 ? (
        <div className="px-4 py-8 text-center text-muted-foreground text-sm">
          閲覧履歴がありません
        </div>
      ) : isLoading ? (
        <div className="px-4 py-8 text-center text-muted-foreground text-sm">
          読み込み中…
        </div>
      ) : isError ? (
        <div className="px-4 py-8 text-center text-destructive text-sm">
          履歴の取得に失敗しました
        </div>
      ) : historyThreads.length === 0 ? (
        <div className="px-4 py-8 text-center text-muted-foreground text-sm">
          閲覧履歴がありません
        </div>
      ) : (
        <ul className="px-2 pb-4 space-y-1">
          {historyThreads.map(
            ({ thread, counts, firstAttachment, boardSlug: entrySlug }) => (
              <li key={thread.id}>
                <Link
                  to="/$boardSlug/thread/$threadId"
                  params={{ boardSlug: entrySlug, threadId: thread.id }}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors"
                >
                  <img
                    src={getImageUrl(firstAttachment, false)}
                    alt=""
                    className="w-12 h-12 rounded object-cover bg-muted flex-none"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-foreground truncate">
                      {getThreadTitle(thread)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {counts}レス
                    </div>
                  </div>
                </Link>
              </li>
            ),
          )}
        </ul>
      )}
    </aside>
  );
};
